/**
 * Workflow brick catalog metadata (mirrors engine/workflows/brick_catalog.js).
 * Used by Kronos when API bricks lack transient inputs/outputs (Engine runtime).
 */

import { normalizeBrickInputsMapping } from './workflowInputsMappingUtils';

/** @typedef {{ inputs?: string[], outputs?: string[], server_side?: boolean }} BrickCatalogMeta */

const DISPLAY_ONLY_FIELD_TYPES = new Set(['static_text', 'static_image']);

const isDisplayOnlyFieldType = (type) => (
    DISPLAY_ONLY_FIELD_TYPES.has(String(type || '').toLowerCase())
);

const WAIT_APPROVAL_OUTPUTS = [
    'approval',
    'approval.decision',
    'approval.is_approved',
    'approval.is_rejected',
    'approval.comment',
    'approval.by',
    'approval.at',
];

/** Static catalog entries aligned with engine/workflows/brick_catalog.js handlers. */
const STATIC_BRICK_CATALOG = {
    'application.create': {
        inputs: ['type_id', 'max_nb_hours', 'max_nb_hours_per_day', 'sectors'],
        outputs: [],
        server_side: true,
    },
    'book.on.ticket': {
        inputs: ['ticket_id', 'screening_id', 'nb', 'api_key'],
        outputs: ['booking_ids', 'bookings', 'bookings_count', 'ticket_id', 'screening_id', 'nb'],
        server_side: true,
    },
    'cart.add.article': {
        inputs: ['article_id', 'variant_id', 'quantity', 'single_line'],
        outputs: ['cart_id', 'cart'],
        server_side: true,
    },
    'cart.set.user.data': {
        inputs: [
            'cart_id',
            'company',
            'title',
            'firstname',
            'lastname',
            'email',
            'address',
            'city',
            'zip',
            'country',
            'age',
            'sex',
            'phone',
            'cellphone',
            'language',
            'birthdate',
            'comment',
        ],
        outputs: ['cart_id', 'cart'],
        server_side: true,
    },
    'email.send': {
        inputs: ['layout_id', 'recipients'],
        outputs: [],
        server_side: true,
    },
    'ticket.create': {
        inputs: [
            'type_id',
            'activation_mode',
            'pricing_key',
            'edition',
            'api_key',
            'company',
            'title',
            'lastname',
            'firstname',
            'email',
            'address',
            'zip',
            'city',
            'country',
            'age',
            'birthdate',
            'photo',
            'gender',
            'phone',
            'cellphone',
            'gift',
            'comment',
        ],
        outputs: ['ticket_id', 'ticket', 'activated'],
        server_side: true,
    },
    'user.update': {
        inputs: [
            'sectors',
            'availabilities',
            'new-password',
            'new-password-confirmation',
            'email',
            'avatar',
            'gender',
            'firstname',
            'lastname',
            'address',
            'zip',
            'city',
            'country',
            'fix_phone',
            'mobile_phone',
            'pro_phone',
            'fax',
            'birthday',
            'avs',
            'remarks',
            'bank',
            'bic',
            'iban',
        ],
        outputs: [],
        server_side: true,
    },
    'user.apply.abilities': {
        inputs: ['template_id', 'user_id'],
        outputs: [],
        server_side: true,
    },
    'wait.payment': {
        inputs: ['cart_id'],
        outputs: ['cart_id'],
        server_side: true,
    },
    'wait.approval': {
        inputs: [],
        outputs: WAIT_APPROVAL_OUTPUTS,
        server_side: true,
    },
    redirect: {
        inputs: [],
        outputs: [],
        server_side: false,
    },
    'message.show': {
        inputs: [],
        outputs: [],
        server_side: false,
    },
    'auth.kronos.login': {
        inputs: [],
        outputs: ['user', 'user_id', 'registered', 'loggedin'],
        server_side: false,
    },
    'form.show': {
        inputs: [],
        outputs: [],
        server_side: false,
    },
    'form.handle': {
        inputs: [],
        outputs: [],
        server_side: true,
    },
    foreach: {
        inputs: [],
        outputs: [],
        server_side: true,
    },
    'context.set.data': {
        inputs: [],
        outputs: [],
        server_side: true,
    },
};

/**
 * @param {unknown} brick
 * @returns {string|null}
 */
function brick_option(brick, key) {
    const options = brick?.options;
    if (options && typeof options === 'object' && options[key] != null) {
        return String(options[key]);
    }
    return null;
}

/**
 * @param {unknown} formLayout
 * @returns {{ inputs: string[], outputs: string[] }}
 */
function workflow_paths_from_form_layout(formLayout) {
    if (!formLayout || typeof formLayout !== 'object') {
        return { inputs: [], outputs: [] };
    }

    const fields = Array.isArray(formLayout.fields) ? formLayout.fields : [];
    const groups_raw = Array.isArray(formLayout.groups) ? formLayout.groups : [];
    const group_name_by_id = {};
    const group_multiple_by_id = {};

    groups_raw.forEach((group) => {
        const id = String(group?.id || group?._id || '').trim();
        const name = String(group?.name || '').trim();
        if (id && name) {
            group_name_by_id[id] = name;
            group_multiple_by_id[id] = !!group?.multiple;
        }
    });

    const inputs = [];
    const outputs = [];

    fields.forEach((field) => {
        if (isDisplayOnlyFieldType(field?.type)) {
            return;
        }
        const field_name = String(field?.name || '').trim();
        if (!field_name) {
            return;
        }

        const group_id = String(field?.groupId ?? field?.group_id ?? '').trim();
        let base = field_name;
        if (group_id && group_name_by_id[group_id]) {
            const gname = group_name_by_id[group_id];
            base = group_multiple_by_id[group_id]
                ? `${gname}.0.${field_name}`
                : `${gname}.${field_name}`;
        }

        inputs.push(field_name);
        outputs.push(base);

        if (String(field?.type || '').toLowerCase() === 'list') {
            outputs.push(`${base}_value`);
            if (field?.multiple) {
                outputs.push(`${base}_count`);
            }
        }
    });

    groups_raw.forEach((group) => {
        const name = String(group?.name || '').trim();
        if (name && group?.multiple) {
            outputs.push(name);
            outputs.push(`${name}_count`);
        }
    });

    return {
        inputs: [...new Set(inputs)],
        outputs: [...new Set(outputs)],
    };
}

/**
 * @param {unknown} brick
 * @param {Record<string, object>|undefined} formsById
 * @returns {object|null}
 */
function resolve_form_layout_for_brick(brick, formsById) {
    const embedded = brick?.resources?.form;
    if (embedded && typeof embedded === 'object') {
        return embedded;
    }

    const form_id = brick_option(brick, 'form_id');
    if (!form_id || !formsById) {
        return null;
    }
    return formsById[String(form_id)] || null;
}

/**
 * @param {unknown} brick
 * @param {{ formsById?: Record<string, object> }} [ctx]
 * @returns {BrickCatalogMeta}
 */
export function brickCatalogMetadata(brick, ctx = {}) {
    const type = String(brick?.type || '').trim();
    const static_meta = STATIC_BRICK_CATALOG[type];
    if (!static_meta) {
        return { inputs: [], outputs: [], server_side: true };
    }

    const meta = {
        inputs: [...(static_meta.inputs || [])],
        outputs: [...(static_meta.outputs || [])],
        server_side: static_meta.server_side,
    };

    if (type === 'form.show' || type === 'form.handle') {
        const layout = resolve_form_layout_for_brick(brick, ctx.formsById);
        const paths = workflow_paths_from_form_layout(layout);
        if (type === 'form.show') {
            meta.inputs = paths.inputs;
            meta.outputs = paths.outputs;
        } else {
            meta.outputs = paths.outputs;
        }
    }

    if (type === 'foreach') {
        const item_key = (brick_option(brick, 'item_key') || 'item').trim() || 'item';
        const index_key = (brick_option(brick, 'index_key') || 'index').trim() || 'index';
        meta.outputs = [...new Set([item_key, index_key].filter(Boolean))];
    }

    if (type === 'context.set.data') {
        const options = brick?.options;
        const keys = [];
        if (options && typeof options === 'object') {
            Object.keys(options).forEach((name) => {
                if (name === 'custom_variables' || name === 'data') {
                    return;
                }
                const value = options[name];
                if (value != null && String(value).trim() !== '') {
                    keys.push(name);
                }
            });
            const custom_rows = Array.isArray(options.custom_variables) ? options.custom_variables : [];
            custom_rows.forEach((row) => {
                const key = row?.key != null ? String(row.key).trim() : '';
                if (key) {
                    keys.push(key);
                }
            });
        }
        meta.outputs = [...new Set(keys)];
    }

    return meta;
}

/**
 * Attach catalog inputs/outputs when missing (Engine API parity for Kronos editor).
 *
 * @param {unknown} brick
 * @param {{ formsById?: Record<string, object> }} [ctx]
 * @returns {unknown}
 */
export function enrichWorkflowBrick(brick, ctx = {}) {
    if (!brick || typeof brick !== 'object' || !brick.type) {
        return brick;
    }

    const meta = brickCatalogMetadata(brick, ctx);
    const has_inputs = Array.isArray(brick.inputs) && brick.inputs.length > 0;
    const has_outputs = Array.isArray(brick.outputs) && brick.outputs.length > 0;

    return {
        ...brick,
        inputs: has_inputs ? brick.inputs : meta.inputs,
        outputs: has_outputs ? brick.outputs : meta.outputs,
        inputs_mapping: normalizeBrickInputsMapping(brick.inputs_mapping),
        server_side: typeof brick.server_side === 'boolean' ? brick.server_side : meta.server_side,
    };
}

/**
 * @param {unknown[]|null|undefined} bricks
 * @param {{ formsById?: Record<string, object> }} [ctx]
 * @returns {unknown[]}
 */
export function enrichWorkflowBricks(bricks, ctx = {}) {
    return (bricks || []).map((brick) => enrichWorkflowBrick(brick, ctx));
}

/**
 * Index forms by id and _id for context-path lookups.
 *
 * @param {unknown[]|null|undefined} forms
 * @returns {Record<string, object>}
 */
export function buildFormsById(forms) {
    const acc = {};
    (forms || []).forEach((form) => {
        if (form?.id != null) {
            acc[String(form.id)] = form;
        }
        if (form?._id != null && acc[String(form._id)] == null) {
            acc[String(form._id)] = form;
        }
    });
    return acc;
}
